import {sleep} from '@benchling/js-utils';
import _ from 'underscore';

import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import * as BioentityDAO from 'bioentities/BioentityDAO';
import requestFetchValidationExtras from 'bioentities/requestFetchValidationExtras';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import {BioentitySummary} from 'folder-items/FileConstants';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {restArrayToGraphQL} from 'graphql-client/declarative-translations/external/translate';
import AppDispatcher from 'lib/Dispatcher';
import MetadataDAO from 'metadata/MetadataDAO';
import {EntrySummary} from 'notebook/ELNConstants';
import notifications from 'notifications';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {SchemaValidationStatus} from 'schemas/SchemaValidationConstants';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

export function load(
  fileId: string,
  {bioentityAliasInfos}: {bioentityAliasInfos: Optional<Array<BioentityAliasInfo>>}
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.INVENTORY_FILE_LOAD,
    fileId,
    bioentityAliasInfos,
  });
}

export function getHoistedTags(fileId: string): void {
  BioentityDAO.getHoistedTags(fileId)
    .done((response) => {
      const {hoistedTagsGraphById} = response!;
      AppDispatcher.dispatch({
        actionType: EventConstants.HOISTED_TAGS_FETCH_COMPLETED,
        fileId,
        hoistedTagsGraphById,
      });
    })
    .fail(notifications.notifyError);
}

export function getTags(fileId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.FILE_TAGS_UPDATE_STARTED,
  });
  FileBrowserDAO.bulkGetTags([fileId])
    .done((response) => {
      const {data} = response!;
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_TAGS_UPDATED,
        tagData: data,
      });
    })
    .fail((error) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_TAGS_UPDATE_FAILED,
      });
      notifications.notifyError(error);
    });
}

export async function loadBioentityAliasInfos(fileIds: Array<string>): Promise<void> {
  AppDispatcher.dispatch({actionType: EventConstants.BIOENTITY_ALIAS_INFO_LOAD});
  const bioentityAliasInfosById = await BioentityDAO.bulkGetAliases(fileIds);
  AppDispatcher.dispatch({
    actionType: EventConstants.BIOENTITY_ALIAS_INFO_LOAD_COMPLETED,
    bioentityAliasInfosById,
  });
}

export function addAlias(fileId: string, alias: string): Promise<void> {
  return BioentityDAO.addAlias(fileId, alias)
    .then((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.BIOENTITY_ALIAS_INFO_ADD_COMPLETED,
        bioentityAliasInfo: response,
        fileId,
      });
      notifications.success('Added alias successfully');
    })
    .catch(notifications.notifyError);
}

export function deleteAlias(fileId: string, alias: string): void {
  BioentityDAO.deleteAlias(fileId, alias)
    .done((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.BIOENTITY_ALIAS_INFO_DELETE_COMPLETED,
        alias,
        fileId,
      });
      notifications.success('Deleted alias successfully');
    })
    .fail(notifications.notifyError);
}

export async function updateAlias(
  fileId: string,
  originalAlias: string,
  newAlias: string,
  dataModsHeader?: DataModificationsHeader
): Promise<string> {
  try {
    await BioentityDAO.updateAlias(fileId, originalAlias, newAlias, dataModsHeader);
    AppDispatcher.dispatch({
      actionType: EventConstants.BIOENTITY_ALIAS_INFO_UPDATE_COMPLETED,
      oldAlias: originalAlias,
      alias: newAlias,
      fileId,
    });
    notifications.success('Updated alias successfully');
    return newAlias;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (err) {
    AppDispatcher.dispatch({
      actionType: EventConstants.BIOENTITY_ALIAS_INFO_UPDATE_FAILED,
    });
    notifications.notifyError(err);
    return originalAlias;
  }
}

export async function updateAliasWithDataMods(
  fileId: string,
  originalAlias: string,
  newAlias: string,
  isRegistered: boolean
): Promise<string> {
  let alias: string;
  await callAsyncWithDataModifications({
    changeSummary: isRegistered
      ? getInventoryRegistryChangeSummary({apiIds: [fileId], action: InventoryRegistryDataModAction.MODIFY})
      : '',
    onModalCancel: () => {
      alias = originalAlias;
    },
    onPerformAction: async (dataModsHeader?) => {
      alias = await updateAlias(fileId, originalAlias, newAlias, dataModsHeader);
    },
    apiIds: [fileId],
    analyticsParams: {
      module: 'REGISTRY',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY && isRegistered,
  });
  return alias!;
}

export function updateRegistryCollections(
  fileIds: Array<string>,
  {
    inOrgCollection,
    inPersonalCollection,
  }: {inOrgCollection: Optional<boolean>; inPersonalCollection: Optional<boolean>}
): void {
  BioentityDAO.updateRegistryCollections(fileIds, {inOrgCollection, inPersonalCollection})
    .done((res) => {
      const response = res!;
      AppDispatcher.dispatch({
        actionType: EventConstants.INVENTORY_FILES_UPDATE_REGISTRY_COLLECTIONS,
        newFileInfo: response,
      });

      for (const id of Object.keys(response)) {
        AppDispatcher.dispatch({
          actionType: EventConstants.FILE_BROWSER_RENAME_FILE,
          id,
          name: response[id].name,
          bioentityAliasInfos: response[id].bioentityAliasInfos,
        });
      }
      notifications.success(
        `Updated registry collections for ${fileIds.length}
         ${benchlingUtil.pluralize(fileIds.length, 'file')}.`
      );
    })
    .fail(notifications.notifyError);
}

export function bulkUpdateTagSchema(
  fileIds: Array<string>,
  schemaId: string | null,
  deleteOldSchemaTags: boolean
): void {
  MetadataDAO.bulkUpdateTagSchema(fileIds, schemaId, deleteOldSchemaTags)
    .done((response) => {
      const storeFiles = getStore().getState().models.fileSummariesById;
      const bioentitySummariesById = Object.keys(response)
        .filter((apiId) => APIUtil.isBiologicalEntity(apiId))
        .reduce((memo: {[key: string]: BioentitySummary}, fileId) => {
          if (response[fileId] && storeFiles[fileId]) {
            memo[fileId] = {...storeFiles[fileId], ...response[fileId]};
          }
          return memo;
        }, {});

      const storeEntries = getStore().getState().models.entrySummariesById;
      const entrySummariesById = Object.keys(response)
        .filter((apiId) => APIUtil.isEntry(apiId))
        .reduce((memo: {[key: string]: EntrySummary}, fileId) => {
          if (response[fileId] && storeEntries[fileId]) {
            memo[fileId] = {...storeEntries[fileId], ...response[fileId]};
          }
          return memo;
        }, {});

      /* [BNCH-11628] Keep redux store in sync with changes to backbone
       * bioentity summaries.
       *
       * Redux by default will do a shallow merge of bioentities when calling
       * the filesFetchCompleted function - replacing full objects on a per-key
       * basis. Ideally we would include all fields (including folders) for
       * bioentities to adhere to the principles of the managed model store for
       * changed entities, but we make an exception because of the performance
       * implications of fetching the folder structure for each bioentity.
       *
       * Instead, our strategy is to only update the redux cache if the fully
       * qualified bioentity is in the store already. Otherwise, we skip the
       * update, and assume it is populated elsewhere. If we replaced the entry
       * completely, we would cause downstream issues for our syncers.
       *
       */
      getStore().dispatch(ModelActions.filesFetchCompleted(bioentitySummariesById, []));
      getStore().dispatch(ModelActions.entriesFetchCompleted(entrySummariesById, []));
      writeFragmentsToCache(
        getFragmentWrapper('BioentitySummary'),
        restArrayToGraphQL('BioentitySummary', Object.values(bioentitySummariesById))
      );
      writeFragmentsToCache(
        getFragmentWrapper('EntrySummary'),
        restArrayToGraphQL('EntrySummary', Object.values(entrySummariesById))
      );

      for (const fileId of Object.keys(response)) {
        AppDispatcher.dispatch({
          actionType: EventConstants.FILE_TAG_SCHEMA_UPDATED,
          fileId,
          tagSchema: response[fileId].tagSchema,
          tags: response[fileId].tags,
        });
        getStore().dispatch({
          type: EventConstants.FILE_TAG_SCHEMA_UPDATED,
          fileId,
          tagSchema: response[fileId].tagSchema,
          tags: response[fileId].tags,
        });
      }
      notifications.success('Schema set successfully.');
    })
    .fail(notifications.notifyError);
}

export async function overrideValidationStatus(bioentityIds: Array<string>): Promise<void> {
  const {schemaValidationStateByBioentityId} = await BioentityDAO.overrideValidationStatus(bioentityIds);
  try {
    AppDispatcher.dispatch({
      actionType: EventConstants.BIOENTITIES_VALIDATION_STATUS_UPDATED,
      schemaValidationStateByBioentityId,
    });
    notifications.success('Successfully overrode validation checks.');
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

function fetchExtras(bioentityIds: Array<string>): void {
  for (const bioentityId of bioentityIds) {
    requestFetchValidationExtras(bioentityId, {forceFetch: true});
  }
}

/**
 * Fetch for validation status every 5 seconds until all statuses are no longer VALIDATING,
 * or after 60 seconds.
 *
 * Fetch extras at the beginning (to clear error records from any formerly-invalid entities), and at the end,
 * once validation state and new error records have been computed.
 */
export async function pollValidationState(bioentityIds: Array<string>): Promise<void> {
  fetchExtras(bioentityIds);
  for (let i = 0; i < 12; i++) {
    const response = await FileBrowserDAO.bulkFetchFileSummaries(bioentityIds);
    const {fileSummariesById} = response!;
    const schemaValidationStateByBioentityId = _.mapObject(
      fileSummariesById,
      (fileSummary) => fileSummary.schemaValidationState
    );
    AppDispatcher.dispatch({
      actionType: EventConstants.BIOENTITIES_VALIDATION_STATUS_UPDATED,
      schemaValidationStateByBioentityId,
    });
    if (
      Object.values(fileSummariesById).every(
        ({schemaValidationState}) =>
          !schemaValidationState || schemaValidationState.status !== SchemaValidationStatus.VALIDATING
      )
    ) {
      fetchExtras(bioentityIds);
      break;
    } else {
      await sleep(5000);
    }
  }
}
